import { Component, Input, ViewChild } from '@angular/core';
import { AttachedFile, BaseEntity, BaseEntityResponse, BaseResponse, FileView, SharingInformation, NotificationTemplateListView, RenderedTemplate } from '@base/entities/BaseEntities';
import { BasePageComponent } from '@base/component/BasePageComponent';
import { ServiceBagService, ServiceBagServices } from '@base/component/ServiceBag';
import { EnumNotificationDeliveryOptions, EnumNotificationTemplateEditingMode, EnumPageDisplayModes, EnumPageEntitySources, EnumPrintingType } from '@core/framework/sysenums.enum';
import { BaseEndpointCRUD } from '@base/endpoint/BaseEndpointCRUD';
import { PrintBoxDialogComponent } from '../printbox-dialog/printboxdialog.component';
import { Utils } from '@m4u-angular/core/helpers/Utils';
import { Observable } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import { NotificationPreviewDialogComponent } from '../../notifications/notification-preview-dialog/notification-preview-dialog.component';

class State {
    IsSupportSharing: boolean;
    IsSupportEmail: boolean;
    IsSupportSMS: boolean;
    IsSupportWhatsapp: boolean;
    PageTemplates: NotificationTemplateListView[] = [];
    SelectedTemplates: NotificationTemplateListView[] = [];
    SelectedTemplate: NotificationTemplateListView;
    SelectedTemplateID: number;
    DeliveryOption: EnumNotificationDeliveryOptions;
    SharingInformation = new SharingInformation;
    PreviewBody: any;
    IsShareByEntities: boolean;
    IsReportAndEntity: boolean;
}

@Component({
    providers: ServiceBagServices,
    selector: 'm4u-share-dialog',
    templateUrl: './share-dialog.component.html',
    styleUrls: ['./share-dialog.component.scss']
})
export class ShareDialogComponent extends BasePageComponent<BaseEntity, State> {

    @Input() ShareEntityType: string;
    @Input() PageName: string;
    @Input() GetRenderedTemplate: (notificationTemplateID: number, deliveryOption: EnumNotificationDeliveryOptions) => Observable<BaseEntityResponse<RenderedTemplate>>;
    @Input() Share: (sharingInformation: SharingInformation) => Observable<BaseEntityResponse<FileView>>;

    @ViewChild(PrintBoxDialogComponent) printBoxDialog: PrintBoxDialogComponent;
    @ViewChild('previewDialog') previewDialog: NotificationPreviewDialogComponent;

    constructor(A: ServiceBagService, private sanitizer: DomSanitizer) {
        super(A, 'ShareDialogComponent', BaseEntity, State, EnumPageDisplayModes.Dialog, EnumPageEntitySources.External);
    }

    protected override OnInitComponentFinished() {
        this.State.PageTemplates = this.A.ShareService.GetNotificationTemplatesByPageAndEntity(this.PageName, this.ShareEntityType);
        this.State.IsReportAndEntity = this.State.PageTemplates.some(x => x.EntityBindingPageType == EnumPrintingType.Report)
            && this.State.PageTemplates.some(x => x.EntityBindingPageType == EnumPrintingType.Entity);

        this.State.IsSupportEmail = this.State.PageTemplates.some(x => x.IsSupportEmail);
        this.State.IsSupportSharing = this.State.PageTemplates.some(x => x.IsSupportSharing);
        this.State.IsSupportSMS = this.State.PageTemplates.some(x => x.IsSupportSMS);
        this.State.IsSupportWhatsapp = this.State.PageTemplates.some(x => x.IsSupportWhatsapp);
    }

    IsEmail = () => this.State.DeliveryOption == EnumNotificationDeliveryOptions.Email;
    IsSms = () => this.State.DeliveryOption == EnumNotificationDeliveryOptions.SMS;
    IsWhatsApp = () => this.State.DeliveryOption == EnumNotificationDeliveryOptions.WhatsApp;
    IsPrint = () => this.State.DeliveryOption == EnumNotificationDeliveryOptions.Print;
    IsExport = () => this.State.DeliveryOption == EnumNotificationDeliveryOptions.ExportToPDF;
    IsShareMobile = () => !this.State.DeliveryOption && this.State.SelectedTemplates?.length > 0;

    IsEditBodyMode = () => this.State.SelectedTemplate.EditingMode == EnumNotificationTemplateEditingMode.EditBody;
    IsEditPrefixMode = () => this.State.SelectedTemplate.EditingMode == EnumNotificationTemplateEditingMode.EditPrefix;
    IsPreviewMode = () => this.State.SelectedTemplate.EditingMode == EnumNotificationTemplateEditingMode.Preview;
    IsHideBodyMode = () => this.State.SelectedTemplate.EditingMode == EnumNotificationTemplateEditingMode.None;

    OnShareMethodChosen(deliveryOptions: EnumNotificationDeliveryOptions) {
        this.State.DeliveryOption = deliveryOptions;
        this.State.SelectedTemplate = null;
        this.State.SelectedTemplateID = null;
        this.State.SharingInformation = new SharingInformation;

        this.FilterTemplates();

        if (this.State.SelectedTemplates.length == 1) {
            this.State.SelectedTemplate = this.State.SelectedTemplates[0];
            this.State.SelectedTemplateID = this.State.SelectedTemplates[0].EntryID;
            this.SetRenderedTemplate();
        }
    }

    OnNotificationTemplateSelected(e) {
        this.State.SelectedTemplate = this.State.SelectedTemplates.find(x => x.EntryID == e.value);
        if (!this.State.SelectedTemplate) return;

        this.SetRenderedTemplate();
    }

    ShareOnMobile() {
        this.ShareBase((fileView) => {
            const file = new File([Uint8Array.from(fileView.FileData)], fileView.FullName, { type: fileView.ContentType });

            navigator.share({
                title: this.RC('lblShareToApplication'),
                files: [file],
            } as ShareData);

            this.CloseDialogWithSuccess();
        }, false);
    }

    OnShowPreview() {
        this.previewDialog.HTMLBody = this.State.SharingInformation.Body;
        this.previewDialog.ShowDialogShort(() => { });
    }

    OnShowForEntitiesChanged(e) {
        if (!e?.event || !this.State.IsReportAndEntity) return;
        this.FilterTemplates();

        if (this.State.SelectedTemplates.length == 1) {
            this.State.SelectedTemplate = this.State.SelectedTemplates[0];
            this.State.SelectedTemplateID = this.State.SelectedTemplates[0].EntryID;
            this.SetRenderedTemplate();
        }
        else {
            this.State.SelectedTemplateID = null;
            this.State.SelectedTemplate = null;
        }
    }

    Print() {
        this.ShareBase((fileView) => {
            this.printBoxDialog.PDFData = Utils.ConvertToBase64(fileView.FileData);
            this.printBoxDialog.ShowDialogShort(() => { });
        }, false);
    }

    Download() {
        this.ShareBase((fileView) => {
            const file = new AttachedFile;
            file.Extension = fileView.Extension;
            file.Name = fileView.Name;
            Utils.DoFileDownload(file, Utils.ConvertToBase64(fileView.FileData), fileView.ContentType);
            this.CloseDialogWithSuccess();
        }, true);
    }

    GetSubmitButtonName() {
        switch (this.State.DeliveryOption) {
            case EnumNotificationDeliveryOptions.ExportToPDF:
                return this.RC('genDownload');
            case EnumNotificationDeliveryOptions.Print:
                return this.RC('genPrint');
            case EnumNotificationDeliveryOptions.SMS:
            case EnumNotificationDeliveryOptions.WhatsApp:
            case EnumNotificationDeliveryOptions.Email:
                return this.RC('btnSend');
        }

        return this.RC('btnShare');
    }

    OnSend() {
        if (!this.Share) return;
        this.State.SharingInformation.DeliveryOption = this.State.DeliveryOption;
        this.State.SharingInformation.NotificationTempalteID = this.State.SelectedTemplateID;
        this.Do(this.Share(this.State.SharingInformation), true, response => {
            this.CloseDialogWithSuccess();
        });
    }

    OnSubmit() {
        switch (this.State.DeliveryOption) {
            case EnumNotificationDeliveryOptions.ExportToPDF:
                this.Download();
                return;
            case EnumNotificationDeliveryOptions.Print:
                this.Print();
                return;
            case EnumNotificationDeliveryOptions.SMS:
            case EnumNotificationDeliveryOptions.WhatsApp:
            case EnumNotificationDeliveryOptions.Email:
                this.OnSend();
                return;
            default:
                this.ShareOnMobile();
        }
    }

    private FilterTemplates() {
        switch (this.State.DeliveryOption) {
            case EnumNotificationDeliveryOptions.Email:
                this.State.SelectedTemplates = this.State.PageTemplates.filter(x => x.IsSupportEmail);
                break;
            case EnumNotificationDeliveryOptions.WhatsApp:
                this.State.SelectedTemplates = this.State.PageTemplates.filter(x => x.IsSupportWhatsapp);
                break;
            case EnumNotificationDeliveryOptions.SMS:
                this.State.SelectedTemplates = this.State.PageTemplates.filter(x => x.IsSupportSMS);
                break;
            case EnumNotificationDeliveryOptions.Print:
            case EnumNotificationDeliveryOptions.ExportToPDF:
            default:
                this.State.SelectedTemplates = this.State.PageTemplates.filter(x => x.IsSupportSharing);
                break;
        }

        if (this.State.IsReportAndEntity) {
            this.State.SelectedTemplates = this.State.SelectedTemplates.filter(x => x.EntityBindingPageType == EnumPrintingType.Report && !this.State.IsShareByEntities ||
                x.EntityBindingPageType == EnumPrintingType.Entity && this.State.IsShareByEntities)
        }
    }

    private ShareBase(callback: (file: FileView) => void, isShowSuccess: boolean) {
        if (!this.Share) return;

        const sharingInformation = new SharingInformation;
        sharingInformation.DeliveryOption = EnumNotificationDeliveryOptions.Share;
        sharingInformation.NotificationTempalteID = this.State.SelectedTemplate.EntryID;

        this.Do(this.Share(sharingInformation), isShowSuccess, response => {
            if (!response.IsSuccess) return;
            callback(response.Entity);
        });
    }

    private SetRenderedTemplate() {
        if (!this.GetRenderedTemplate ||
            this.State.DeliveryOption == EnumNotificationDeliveryOptions.Print ||
            this.State.DeliveryOption == EnumNotificationDeliveryOptions.Share ||
            this.State.DeliveryOption == EnumNotificationDeliveryOptions.ExportToPDF)
            return;

        this.Do(this.GetRenderedTemplate(this.State.SelectedTemplate.EntryID, this.State.DeliveryOption), false, response => {
            if (!response.IsSuccess) return;
            this.State.SharingInformation.Subject = response.Entity?.Subject;
            this.State.SharingInformation.Body = response.Entity?.Body;
            this.State.SharingInformation.CcAddress = response.Entity?.CcAddress;
            this.State.SharingInformation.BccAddress = response.Entity?.BccAddress;

            if (this.IsEmail() && this.IsPreviewMode()) {
                this.State.PreviewBody = this.sanitizer.bypassSecurityTrustHtml(this.State.SharingInformation.Body) as any;
            }
        });
    }
}
